import { useShallow } from 'zustand/react/shallow';
import useHandleApiError from '../../../hooks/useHandleApiError';
import { saleItemIsKit } from '../../../modules/catalogs/utils/saleItemType.utils';
import useOpenProductDetails from '../../../modules/products/products-details-v2/hooks/useOpenProductDetails';
import useSharedEntities from '../../../modules/shared-entities/hooks/useSharedEntities';
import { useTranslation } from '../../../modules/translation/TranslationContext';
import useOrderBookStore from '../../../stores/OrderBook/orderBook.store';
import useProductDetailsStore from '../../../stores/ProductDetails/productDetails.store';
import { ProductDetailsOriginType } from '../../../stores/ProductDetails/productDetails.types';
import { ProductCardActions, UseProductCardActionsParams } from './useProductCardActions.types';

const useProductCardActions = ({
  entityRelations,
  catalogId,
  suggestedOrderActions,
}: UseProductCardActionsParams): ProductCardActions => {
  const [
    isShoppingFn,
    toggleShopping,
    toggleShoppingKit,
    loading,
    selectedOrder,
    orderProductsIds,
  ] = useOrderBookStore(
    useShallow((state) => [
      state.isShopping,
      state.toggleShopping,
      state.toggleShoppingKit,
      state.loading,
      state.selectedOrder,
      state.orderProductsIds,
    ]),
  );

  const { getApiErrorMessage } = useHandleApiError();
  const isSuggestedOrder = selectedOrder?.status === 'SUGGESTED';
  const { translate } = useTranslation();
  const setFrom = useProductDetailsStore((productDetailsState) => productDetailsState.setFrom);
  const { openProductDetails } = useOpenProductDetails();

  const { disableProductDetails } = useSharedEntities({});

  const openDetails = (productColorId?: string) => {
    if (disableProductDetails) {
      return;
    }

    setFrom(catalogId ? { type: ProductDetailsOriginType.CATALOG, id: catalogId } : undefined);

    if (saleItemIsKit(entityRelations)) {
      openProductDetails({ kitId: entityRelations.kitId });
      return;
    }

    openProductDetails({
      productId: entityRelations.productId,
      productColorId: productColorId || entityRelations.productColorId,
    });
  };

  const onClickShopping = () => {
    if (saleItemIsKit(entityRelations)) {
      toggleShoppingKit({
        kitId: entityRelations.kitId,
        translate,
        getApiErrorMessage,
      });
      return;
    }

    if (isSuggestedOrder && suggestedOrderActions) {
      toggleShopping({
        translate,
        productColorId: entityRelations.productColorId,
        onSuccess: suggestedOrderActions.addProductColor,
        onFailure: suggestedOrderActions.removeProductColor,
      });
      return;
    }

    toggleShopping({
      productColorId: entityRelations.productColorId,
      translate,
    });
  };

  const isShopping = saleItemIsKit(entityRelations)
    ? isShoppingFn(entityRelations.kitId, orderProductsIds)
    : isShoppingFn(entityRelations.productColorId, orderProductsIds);

  return {
    isShopping,
    loading,
    onClickShopping,
    openDetails,
    disableProductDetails,
  };
};

export default useProductCardActions;
